import { useEffect, useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice } from "@/store/storeCart";
import { Loader2 } from "lucide-react";

const REFUND_WINDOW_DAYS = 14;
const AUTO_APPROVE_CENTS = 2500; // $25 — Wavlynq Store policy

const REASON_OPTIONS = [
  { value: "not_as_described", label: "Item not as described" },
  { value: "damaged", label: "Arrived damaged or defective" },
  { value: "never_arrived", label: "Never arrived" },
  { value: "wrong_item", label: "Wrong item received" },
  { value: "changed_mind", label: "Changed my mind" },
  { value: "other", label: "Other" },
] as const;

const refundSchema = z.object({
  reason: z.enum([
    "not_as_described",
    "damaged",
    "never_arrived",
    "wrong_item",
    "changed_mind",
    "other",
  ]),
  details: z
    .string()
    .trim()
    .max(1000, { message: "Details must be under 1000 characters" })
    .optional(),
});

type Order = {
  id: string;
  order_number: string;
  status: string;
  total_cents: number;
  delivered_at: string | null;
  shipped_at: string | null;
  created_at: string;
};

type ExistingRequest = {
  id: string;
  status: "pending" | "approved" | "denied" | "refunded" | "cancelled";
  reason: string;
  amount_cents: number;
  created_at: string;
};

type Eligibility =
  | { eligible: true }
  | { eligible: false; reason: string };

function evaluateEligibility(order: Order, existing: ExistingRequest | null): Eligibility {
  if (existing && ["pending", "approved", "refunded"].includes(existing.status)) {
    return {
      eligible: false,
      reason:
        existing.status === "refunded"
          ? "This order has already been refunded."
          : existing.status === "approved"
            ? "A refund for this order has been approved and is being processed."
            : "A refund request for this order is already pending review.",
    };
  }
  if (["cancelled", "refunded", "failed"].includes(order.status)) {
    return { eligible: false, reason: `Order is ${order.status} — no refund possible.` };
  }
  if (order.status === "pending_payment") {
    return { eligible: false, reason: "Payment hasn't been captured yet." };
  }
  // Refund window starts at delivery. If not delivered, allow only for "never_arrived"
  // claims after a reasonable wait; we still gate the button on a delivery-or-shipped
  // status so buyers don't file before the vendor has even fulfilled.
  const windowStart = order.delivered_at
    ? new Date(order.delivered_at)
    : order.shipped_at
      ? new Date(order.shipped_at)
      : null;
  if (!windowStart) {
    return {
      eligible: false,
      reason:
        "Refund window opens once the order has shipped. Contact the vendor if there's an issue.",
    };
  }
  const ageDays = (Date.now() - windowStart.getTime()) / (1000 * 60 * 60 * 24);
  if (ageDays > REFUND_WINDOW_DAYS) {
    return {
      eligible: false,
      reason: `Refund window closed (${REFUND_WINDOW_DAYS} days from ${
        order.delivered_at ? "delivery" : "shipment"
      }).`,
    };
  }
  return { eligible: true };
}

interface Props {
  order: Order;
}

const RefundRequestButton = ({ order }: Props) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [existing, setExisting] = useState<ExistingRequest | null>(null);
  const [reason, setReason] = useState<(typeof REASON_OPTIONS)[number]["value"]>(
    "not_as_described",
  );
  const [details, setDetails] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("refund_requests")
        .select("id, status, reason, amount_cents, created_at")
        .eq("reference_kind", "order")
        .eq("reference_id", order.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      setExisting((data as ExistingRequest | null) ?? null);
      setLoadingExisting(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [order.id]);

  const eligibility = evaluateEligibility(order, existing);
  const willAutoApprove = order.total_cents <= AUTO_APPROVE_CENTS;

  const submit = async () => {
    const parsed = refundSchema.safeParse({ reason, details });
    if (!parsed.success) {
      toast({
        title: "Check your input",
        description: parsed.error.issues[0]?.message ?? "Invalid input",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) {
      setSubmitting(false);
      toast({ title: "Sign in required", variant: "destructive" });
      return;
    }
    const { data, error } = await supabase
      .from("refund_requests")
      .insert({
        user_id: u.user.id,
        reference_kind: "order",
        reference_id: order.id,
        amount_cents: order.total_cents,
        currency: "usd",
        reason: parsed.data.reason,
        details: parsed.data.details || null,
      })
      .select("id, status, reason, amount_cents, created_at")
      .single();
    setSubmitting(false);
    if (error) {
      toast({
        title: "Couldn't submit refund request",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    setExisting(data as ExistingRequest);
    setOpen(false);
    toast({
      title: "Refund request submitted",
      description: willAutoApprove
        ? "Under $25 — eligible for fast-track review. Expect a decision within 24 hours."
        : "We'll review and respond by email within 3 business days.",
    });
  };

  if (loadingExisting) {
    return (
      <Button variant="outline" disabled>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </Button>
    );
  }

  if (existing && ["pending", "approved", "refunded", "denied"].includes(existing.status)) {
    const variant: Record<string, string> = {
      pending: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
      approved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
      refunded: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
      denied: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
    };
    return (
      <div className="flex items-center gap-2">
        <Badge className={variant[existing.status]}>
          Refund {existing.status}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {formatPrice(existing.amount_cents)} · {new Date(existing.created_at).toLocaleDateString()}
        </span>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={!eligibility.eligible}>
          Request refund
        </Button>
      </DialogTrigger>
      {!eligibility.eligible && "reason" in eligibility && (
        <p className="text-xs text-muted-foreground mt-2">{eligibility.reason}</p>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request a refund</DialogTitle>
          <DialogDescription>
            Order {order.order_number} · {formatPrice(order.total_cents)}
            {willAutoApprove && (
              <span className="block mt-1 text-emerald-600 dark:text-emerald-400">
                Under $25 — fast-track review.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="refund-reason">Reason</Label>
            <Select value={reason} onValueChange={(v) => setReason(v as typeof reason)}>
              <SelectTrigger id="refund-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REASON_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="refund-details">Details (optional)</Label>
            <Textarea
              id="refund-details"
              value={details}
              maxLength={1000}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Anything that helps us review — photos, order specifics, vendor reply…"
              rows={4}
            />
            <p className="text-xs text-muted-foreground text-right">
              {details.length}/1000
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            By submitting, you confirm this order meets the 14-day refund window. Stripe
            processing fees are not refunded by the vendor.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RefundRequestButton;
